/* =====================================================================================
   Centre record — the full page behind a dot on the map.

   Previously clicking a centre from search landed on /atlas/site/:id, which no route
   handled, so you got the Atlas page with nothing selected and it read as a blank screen.

   This is a page rather than the drawer because a centre record is where the work happens:
   correcting a figure, reading what the last visit found, seeing what the country's
   reimbursement route is. A drawer is for a glance; this is for twenty minutes.

   The correction mechanism is the important part. Every figure here is derived, and the
   person who visited last month knows things no registry publishes. See migration 0015:
   what they enter is never overwritten by an automated refresh.
   ===================================================================================== */

import { useEffect, useMemo, useState } from 'react';
import { fetchSitePeople, addSitePerson, type SitePerson } from '../lib/people';
import { fetchCompleteness, setIncumbent, setSendOuts, type Completeness } from '../lib/completeness';
import { useParams, useNavigate } from 'react-router-dom';
/* Data from the tenant, helpers from atlas.core. Neither from atlas.ts, whose module scope
   carries the whole transplant seed. */
import { siteTotal, placed } from '../data/atlas.core';

/* The tenant names its own segments; shared code does not hold a lookup table of organs. */
const segLabel = (k: string): string => TENANT.vocabulary.segment?.labels?.[k] ?? k;
import React from 'react';
import { TENANT } from '@tenant';

const ALL_SITES = TENANT.seed.site;
const ALL_COUNTRIES = TENANT.seed.country;
const ALL_PATHWAYS = TENANT.seed.pathway;
import type { Site, Confidence } from '../data/atlas';
import {
  fetchOverrides, saveOverride, withdrawOverride, type Override,
} from '../lib/overrides';
import { CaptureIntelligence } from '../components/Capture';
import { dbSiteId, isUuid } from '../lib/ids';
import { EditRecord } from '../components/EditRecord';
import { updateIntelligence, updateInteraction } from '../lib/technical';
import { TechnicalPanel } from '@technical';
import { NewOpportunity, LogInteraction } from '../components/CreateForms';
import { fetchInteractions, type Interaction } from '../lib/pipeline';
import { fetchSiteCommercial, type SiteCommercial } from '../lib/commercial';
import { Loading } from '../components/Loading';
import { useList } from '../lib/useDb';
import './sitepage.css';
import { onFail } from '../lib/errors';
import { fetchAccountForSite, createAccountFromSite } from '../lib/customers';
import { RecordEvidence } from '../components/RecordEvidence';
import { useMarket } from '../components/MarketBar';

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-GB'));

function Conf({ c }: { c: Confidence }) {
  return <span className={`conf conf-${c}`} title={`Confidence: ${c}`}>{c}</span>;
}

/* -------------------------------------------------------------------------------------
   Correction form
   ------------------------------------------------------------------------------------- */
function CorrectionForm({
  site, field, organ, current, onSaved, onCancel,
}: {
  site: Site; field: string; organ: string | null; current: number | null;
  onSaved: () => void; onCancel: () => void;
}) {
  const [value, setValue] = useState(current != null ? String(current) : '');
  const [rationale, setRationale] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null); setBusy(true);
    try {
      await saveOverride({
        siteId: site.id, field, organ, value: Number(value), rationale, source,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="correction card">
      <div className="correction-head">
        Correcting <strong>{organ ? segLabel(organ) : field}</strong>
        {current != null && <span className="correction-was"> · currently {fmt(current)}</span>}
      </div>
      <div className="correction-row">
        <label>
          <span>Correct figure</span>
          <input type="number" value={value} disabled={busy}
                 onChange={(e) => setValue(e.target.value)} />
        </label>
        <label className="grow">
          <span>Where it came from</span>
          <input placeholder="Site visit, lab director, tender document" value={source}
                 disabled={busy} onChange={(e) => setSource(e.target.value)} />
        </label>
      </div>
      <label className="correction-why">
        <span>Why. Required, and the most useful part of the record.</span>
        <textarea rows={2} value={rationale} disabled={busy}
                  placeholder="What were you told, by whom, and when?"
                  onChange={(e) => setRationale(e.target.value)} />
      </label>
      {err && <p className="correction-err" role="alert">{err}</p>}
      <div className="correction-actions">
        <button className="btn-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn-save" onClick={save}
                disabled={busy || !value || rationale.trim().length < 10}>
          {busy ? 'Saving' : 'Save correction'}
        </button>
      </div>
      <p className="correction-note">
        This will not be overwritten by an automated refresh. If a later figure disagrees
        with yours, it is raised for your attention rather than applied.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------------------
   The page
   ------------------------------------------------------------------------------------- */
export function SitePage() {
  /* The market lens, for the routes list and the market column on volumes. Empty for a
     single-market tenant, in which case neither changes. */
  const { markets, marketId } = useMarket();
  const marketCode = marketId ? (markets.find((m) => m.id === marketId)?.code ?? null) : null;
  const marketName = (code?: string) => markets.find((m) => m.code === code)?.name ?? code ?? '';

  const { id } = useParams();
  const navigate = useNavigate();
  const site = useMemo(() => ALL_SITES.find((s) => s.id === id), [id]);

  const [overrides, setOverrides] = useState<Override[]>([]);
  const [editing, setEditing] = useState<{ field: string; organ: string | null; current: number | null } | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [creatingOpp, setCreatingOpp] = useState(false);
  const [logging, setLogging] = useState(false);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [editingIntel, setEditingIntel] = useState<Record<string, unknown> | null>(null);
  const [editingInteraction, setEditingInteraction] = useState<Record<string, unknown> | null>(null);
  const [commercial, setCommercial] = useState<SiteCommercial[]>([]);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [people, setPeople] = useState<SitePerson[] | null>(null);
  const [comp, setComp] = useState<Completeness | null>(null);
  const [addingPerson, setAddingPerson] = useState(false);
  const [np, setNp] = useState({ title: 'Dr', firstName: '', lastName: '', jobTitle: '',
                                 department: '', email: '', phone: '' });
  const [quick, setQuick] = useState<'incumbent' | 'sendout' | null>(null);
  const [quickVal, setQuickVal] = useState('');
  const reload = () => setNonce((n) => n + 1);

  useEffect(() => {
    if (!site) return;
    fetchOverrides(site.id).then(setOverrides).catch(onFail('Overrides', () => setOverrides([])));
    fetchInteractions({ siteId: site.id }).then(setInteractions).catch(onFail('Interactions', () => setInteractions([])));
    setLoading(true);
    fetchSiteCommercial(site.id).then(setCommercial).catch(onFail('Interactions', () => setCommercial([])))
      .finally(() => setLoading(false));
    fetchSitePeople(site.id).then(setPeople).catch(onFail('People', () => setPeople([])));
    fetchCompleteness(site.id).then(setComp).catch(() => setComp(null));
    const dbId = isUuid(site.id) ? site.id : (dbSiteId(site.id) ?? site.id);
    fetchAccountForSite(dbId).then(setCustomerAccount).catch(() => setCustomerAccount(null));
  }, [site, nonce]);

  /* Matched on BOTH shapes. A record still sitting in the local queue carries camelCase keys
     and whatever id the form had; once synced it comes back snake_case with the converted uuid.
     Filtering on one shape alone meant synced observations never appeared on the page they were
     recorded against, which looks exactly like the write having failed. */
  const [customerAccount, setCustomerAccount] = useState<{ id: string; name: string; status: string } | null>(null);
  const [customerMsg, setCustomerMsg] = useState<string | null>(null);
  const siteDbId = site ? dbSiteId(site.id) : null;
  const intel = useList<any>('intelligence').filter((i) => {
    const oid = i.object_id ?? i.objectId;
    return oid === site?.id || (siteDbId != null && oid === siteDbId);
  });

  if (!site) {
    return (
      <div className="page">
        <div className="page-head">
          <h1 className="page-title">{TENANT.vocabulary.site} not found</h1>
          <p className="page-sub">
            No {TENANT.vocabulary.site.toLowerCase()} with the reference <code>{id}</code>. It may have been merged with
            another record: Bari and Birmingham were both held twice until recently.
          </p>
        </div>
        <button className="btn-save" onClick={() => navigate('/atlas')}>Back to the Atlas</button>
      </div>
    );
  }

  const country = ALL_COUNTRIES.find((c) => c.iso3 === site.iso3);
  const pathways = ALL_PATHWAYS.filter((p) => p.iso3 === site.iso3
    && (!marketCode || !p.market || p.market === marketCode));
  /* A multi-market tenant's volumes say which market each was counted in. The column appears
     only when at least one does, so a single-market tenant's table is unchanged. */
  const volumesCarryMarket = site.volumes.some((v) => v.market);
  const ovFor = (field: string, organ: string | null) =>
    overrides.find((o) => o.field === field && (o.value_organ ?? null) === organ);

  return (
    <div className="page sitepage">
      <button className="back-link" onClick={() => navigate('/atlas')}>← Atlas</button>

      <header className="site-head">
        <div>
          <div className="site-eyebrow">
            {[site.city, site.region, country?.name].filter(Boolean).join(' · ')}
          </div>
          <h1 className="site-title display">{site.name}</h1>
          <div className="site-tags">
            <span className={`pill tier-${site.tier}`}>Tier {site.tier}</span>
            {TENANT.vocabulary.siteKinds.filter((k) => site.kinds.includes(k.key as never))
              .map((k) => (
                <span key={k.key} className={`pill ${k.pill}`}>
                  {/* The primary kind is named with the tenant's own word for a site, so it
                      reads "Centre" for one customer and "Hospital" for another. */}
                  {k.label === '@site' ? TENANT.vocabulary.site : k.label}
                </span>
              ))}
            {customerAccount && (
              <span className="pill ok" title={`Customer account: ${customerAccount.name}`}>
                Customer · {customerAccount.status}
              </span>
            )}
            {site.paediatric && <span className="pill info">Paediatric</span>}
            {site.geo === 'approx' && (
              <span className="pill warn" title="Placed at a country centroid, not a real address">
                Location approximate
              </span>
            )}
          </div>
        </div>
        <div className="site-headline">
          <div className="hl-n">{fmt(siteTotal(site))}</div>
          <div className="hl-l">{TENANT.vocabulary.volumeLabel.toLowerCase()}</div>
        </div>
      </header>

      <div className="site-commercial-row">
        {customerAccount ? (
          <span className="muted">
            Commercial record: <strong>{customerAccount.name}</strong> ({customerAccount.status}) in Customers.
            Atlas-owned fields on it follow this page automatically.
          </span>
        ) : (
          <>
            <span className="muted">No commercial record yet.</span>
            <button className="btn" onClick={() => {
              const dbId = isUuid(site.id) ? site.id : (dbSiteId(site.id) ?? site.id);
              createAccountFromSite(dbId)
                .then(() => { setCustomerMsg('Customer account created from this record; find it in Customers, prefilled and linked.');
                  fetchAccountForSite(dbId).then(setCustomerAccount).catch(() => null); })
                .catch((e) => setCustomerMsg(String((e as Error).message)));
            }}>Make customer</button>
          </>
        )}
        {customerMsg && <span className="block-note site-cnote">{customerMsg}</span>}
      </div>

      <div className="site-grid">
        <div className="site-main">
          {/* --- what we still do not know ---
              Deliberately near the top. At the bottom it is a footnote; here it is the first
              thing somebody preparing for a call reads, which is when the gap is cheapest to
              close. Average completeness across the model was 5 per cent when this was built,
              and not one site recorded who supplies it. */}
          {comp && comp.missing.length > 0 && (
            <section className="site-block gap-block">
              <h2>What we still do not know
                <span className="gap-pct">{comp.completeness}% known</span>
              </h2>
              <p className="gap-list">{comp.missing.join(' · ')}</p>
              <div className="gap-actions">
                {comp.missing.includes('who supplies them') && (
                  quick === 'incumbent' ? (
                    <span className="gap-inline">
                      <input className="input grow" autoFocus placeholder="Who supplies them today"
                             value={quickVal} onChange={(e) => setQuickVal(e.target.value)} />
                      <button className="btn primary" onClick={async () => {
                        try {
                          await setIncumbent(site.id, quickVal.split(','));
                          setQuick(null); setQuickVal(''); setNonce((n) => n + 1);
                          setComp(await fetchCompleteness(site.id));
                        } catch (e) { alert(String((e as Error).message)); }
                      }}>Save</button>
                      <button className="btn" onClick={() => setQuick(null)}>Cancel</button>
                    </span>
                  ) : (
                    <button className="btn" onClick={() => { setQuick('incumbent'); setQuickVal(''); }}>
                      Record who supplies them
                    </button>
                  )
                )}
                {comp.missing.includes('what they send out') && quick !== 'incumbent' && (
                  quick === 'sendout' ? (
                    <span className="gap-inline">
                      <input className="input grow" autoFocus placeholder="Tests they send away"
                             value={quickVal} onChange={(e) => setQuickVal(e.target.value)} />
                      <button className="btn primary" onClick={async () => {
                        try {
                          await setSendOuts(site.id, quickVal.split(','));
                          setQuick(null); setQuickVal(''); setNonce((n) => n + 1);
                          setComp(await fetchCompleteness(site.id));
                        } catch (e) { alert(String((e as Error).message)); }
                      }}>Save</button>
                      <button className="btn" onClick={() => setQuick(null)}>Cancel</button>
                    </span>
                  ) : (
                    <button className="btn" onClick={() => { setQuick('sendout'); setQuickVal(''); }}>
                      Record what they send out
                    </button>
                  )
                )}
              </div>
            </section>
          )}

          {/* --- the people --- */}
          <section className="site-block">
            <h2>Clinicians
              <button className="link-btn site-add-person"
                      onClick={() => setAddingPerson(!addingPerson)}>
                {addingPerson ? 'cancel' : 'add someone'}
              </button>
            </h2>

            {addingPerson && (
              /* Three name fields, not one. full_name is generated in the database from title,
                 first and last, and splitting a typed name on the last space gets "van der Berg"
                 and "de Marco" wrong. Asking is cheaper than guessing. */
              <div className="site-person-form">
                <input className="input narrow" placeholder="Title" value={np.title}
                       onChange={(e) => setNp({ ...np, title: e.target.value })} />
                <input className="input" placeholder="First name" value={np.firstName}
                       onChange={(e) => setNp({ ...np, firstName: e.target.value })} />
                <input className="input" placeholder="Last name" value={np.lastName}
                       onChange={(e) => setNp({ ...np, lastName: e.target.value })} />
                <input className="input grow" placeholder="Role, e.g. clinical lead for transplant"
                       value={np.jobTitle}
                       onChange={(e) => setNp({ ...np, jobTitle: e.target.value })} />
                <input className="input" placeholder="Department" value={np.department}
                       onChange={(e) => setNp({ ...np, department: e.target.value })} />
                <input className="input" placeholder="Work email" value={np.email}
                       onChange={(e) => setNp({ ...np, email: e.target.value })} />
                <input className="input" placeholder="Work telephone" value={np.phone}
                       onChange={(e) => setNp({ ...np, phone: e.target.value })} />
                <button className="btn primary" onClick={async () => {
                  try {
                    await addSitePerson(site.id, np);
                    setNp({ title: 'Dr', firstName: '', lastName: '', jobTitle: '',
                            department: '', email: '', phone: '' });
                    setAddingPerson(false);
                    setPeople(await fetchSitePeople(site.id));
                    setComp(await fetchCompleteness(site.id));
                  } catch (e) { alert(String((e as Error).message)); }
                }}>Save</button>
                <p className="site-person-note">
                  Held under legitimate interests. If you contact them, the privacy notice at
                  scirabio.com/privacy-professionals must go in that first message; the record will
                  show it as outstanding until it does.
                </p>
              </div>
            )}
            {people == null ? (
              <p className="muted">Loading</p>
            ) : people.length === 0 ? (
              /* An empty state that says which, because "no clinicians" and "we have not looked
                 yet" are different facts and only one of them is about the centre. */
              <p className="muted">
                No clinicians recorded here yet. Contacts are added from published sources and
                each one carries its source and the date it was checked.
              </p>
            ) : (
              /* Scrolls inside its own block. This table is the widest thing on the record,
                 and a table wider than its grid track overflows invisibly: on a tablet the
                 SOURCE column ran clean under the intelligence rail. */
              <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th><th>Role</th><th>Contact</th><th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="cell-title">
                          {[p.title, p.full_name].filter(Boolean).join(' ')}
                          {p.credentials && <span className="muted"> {p.credentials}</span>}
                        </div>
                        {p.organ_focus?.length ? (
                          <div className="cell-sub">{p.organ_focus.join(', ')}</div>
                        ) : null}
                      </td>
                      <td>
                        <div className="cell-title">{p.role_title || p.job_title || ''}</div>
                        {p.department && <div className="cell-sub">{p.department}</div>}
                        {p.status === 'moved' && (
                          <div className="cell-sub warn">Reported to have moved</div>
                        )}
                      </td>
                      <td>
                        {p.work_email
                          ? <a href={`mailto:${p.work_email}`}>{p.work_email}</a>
                          : <span className="muted">no email recorded</span>}
                        {p.work_phone && <div className="cell-sub">{p.work_phone}</div>}
                        {/* Article 14: we did not get this from them, so they have to be told.
                            Shown on the record so an unnotified contact cannot be used by
                            accident. */}
                        {!p.privacy_notice_sent_at && (p.work_email || p.work_phone) && (
                          <div className="cell-sub warn">Privacy notice not yet sent</div>
                        )}
                      </td>
                      <td className="cell-sub">
                        {p.source_url
                          ? <a href={p.source_url} target="_blank" rel="noreferrer">
                              {p.data_source || 'source'}
                            </a>
                          : <span className="warn">no source recorded</span>}
                        {p.confidence && <div>{p.confidence}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            <p className="site-note">
              The centre&rsquo;s address is above. Clinician details are held under legitimate
              interests, refreshed quarterly because people move, and anyone can be marked
              do-not-contact, which suppresses them here and in any export.
            </p>
          </section>

          {/* Documents that mention this centre by name, with the sentence and the page. Above
              the volumes rather than below, because a figure and the source that disagrees with
              it are worth seeing together. */}
          <RecordEvidence kind="site" id={id!} />

          {/* --- volumes --- */}
          <section className="site-block">
            {/* Only meaningful where the tenant has a segmentation dimension. */}
            <h2>{TENANT.vocabulary.segment ? `Activity by ${TENANT.vocabulary.segment.singular}` : 'Activity'}</h2>
            <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{TENANT.vocabulary.segment ? TENANT.vocabulary.segment.singular.replace(/^./, (c) => c.toUpperCase()) : 'Category'}</th>{volumesCarryMarket && <th>Market</th>}<th className="num">Total</th>{TENANT.vocabulary.volumeSubMetric
                    && <th className="num">{TENANT.vocabulary.volumeSubMetric}</th>}
                  <th>Year</th><th>Confidence</th><th />
                </tr>
              </thead>
              <tbody>
                {site.volumes.map((v) => {
                  const ov = ovFor('volume', v.organ);
                  const shown = ov?.value_numeric ?? v.total;
                  return (
                    <tr key={`${v.organ}:${v.market ?? ''}`} className={ov ? 'row-overridden' : ''}>
                      <td>{TENANT.vocabulary.segment
                        ? segLabel(v.organ)
                        : TENANT.vocabulary.volumeLabel}</td>
                      {volumesCarryMarket && <td className="muted">{marketName(v.market)}</td>}
                      <td className="num"><strong>{fmt(shown)}</strong>
                        {ov && <span className="was">was {fmt(v.total)}</span>}
                      </td>
                      {TENANT.vocabulary.volumeSubMetric &&
                        <td className="num muted">{v.living != null ? fmt(v.living) : '—'}</td>}
                      <td className="muted">{v.year}</td>
                      <td>{ov ? <span className="conf conf-verified">verified</span> : <Conf c={v.confidence} />}</td>
                      <td className="num">
                        <button className="link-btn"
                                onClick={() => setEditing({ field: 'volume', organ: v.organ, current: shown })}>
                          {ov ? 'Revise' : 'Correct'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {editing && (
              <CorrectionForm
                site={site} field={editing.field} organ={editing.organ} current={editing.current}
                onCancel={() => setEditing(null)}
                onSaved={() => { setEditing(null); reload(); }}
              />
            )}
          </section>

          {/* --- prevalence --- */}
          {site.prevalentGrafts && (
            <section className="site-block">
              <h2>{TENANT.vocabulary.population}</h2>
              <p className="block-lead">
                The addressable base for a surveillance assay. Not the transplant rate: the
                number of people walking around with a functioning graft, which is roughly
                eight times larger.
              </p>
              <div className="prev-total">
                <span className="prev-n">{fmt(site.prevalentGrafts.value)}</span>
                <Conf c={site.prevalentGrafts.confidence} />
              </div>
              {site.prevalentByOrgan && (
                <div className="prev-split">
                  {Object.entries(site.prevalentByOrgan)
                    .sort((a, b) => b[1] - a[1])
                    .map(([organ, n]) => (
                      <div className="prev-chip" key={organ}>
                        <span className="prev-chip-l">{segLabel(organ)}</span>
                        <span className="prev-chip-n">{fmt(n)}</span>
                      </div>
                    ))}
                </div>
              )}
              <p className="method-note">{site.prevalentGrafts.method}</p>
            </section>
          )}

          {site.labNote && (
            <section className="site-block">
              <h2>Laboratory</h2>
              <p className="block-text">{site.labNote}</p>
            </section>
          )}

          {/* Only kinds the tenant marks `technical` get an equipment profile. A transplant
              unit with no laboratory of its own has nothing to record, and neither does a
              hospital that buys a capital platform rather than running assays. */}
          {TENANT.vocabulary.siteKinds.some(
            (k) => k.technical && site.kinds.includes(k.key as never)) && (
            <TechnicalPanel siteId={site.id} siteName={site.short || site.name} />
          )}

          {site.note && (
            <section className="site-block">
              <h2>Commercial assessment</h2>
              <p className="block-text">{site.note}</p>
            </section>
          )}

          {/* --- corrections made here --- */}
          {overrides.length > 0 && (
            <section className="site-block">
              <h2>Corrections by the team</h2>
              <p className="block-lead">
                Entered by people who know this centre. Automated refreshes cannot overwrite
                these; where a later figure disagrees it is raised with whoever entered it.
              </p>
              {overrides.map((o) => (
                <article key={o.id} className="card override">
                  <div className="override-head">
                    <strong>
                      {o.value_organ ? segLabel(o.value_organ) : o.field}
                      {' '}&rarr; {fmt(o.value_numeric)}
                    </strong>
                    <button className="link-btn"
                            onClick={() => withdrawOverride(o.id).then(reload)}>Withdraw</button>
                  </div>
                  <p className="override-why">{o.rationale}</p>
                  <div className="override-foot">
                    {o.set_by_name ?? 'A colleague'}
                    {o.source ? ` · ${o.source}` : ''}
                    {' · '}observed {new Date(o.observed_on).toLocaleDateString('en-GB')}
                  </div>
                </article>
              ))}
            </section>
          )}

          {/* The commercial chain. A centre that has an opportunity, a contract and unpaid
              invoices against it is a different conversation from a cold one, and that used
              to require opening three other modules to discover. */}
          {loading && <Loading label="Checking commercial position" />}
          {!loading && commercial.length > 0 && (
            <section className="site-block">
              <h2>Commercial position</h2>
              <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>Opportunity</th><th>Stage</th><th className="num">Value</th>
                      <th>Contract</th><th className="num">Invoiced</th><th className="num">Outstanding</th></tr>
                </thead>
                <tbody>
                  {commercial.map((c) => (
                    <tr key={c.opportunity_id}>
                      <td>
                        <button className="link-btn" onClick={() => navigate('/pipeline')}>
                          {c.opportunity_name}
                        </button>
                      </td>
                      <td><span className="pill mute">{c.opportunity_status}</span></td>
                      <td className="num">{c.amount != null
                        ? `${c.currency} ${Number(c.amount).toLocaleString('en-GB')}` : '—'}</td>
                      <td>
                        {c.contract_reference
                          ? <button className="link-btn" onClick={() => navigate('/contracts')}>
                              <span className="mono">{c.contract_reference}</span>
                            </button>
                          : <span className="muted">none yet</span>}
                      </td>
                      <td className="num">
                        {c.invoice_count > 0
                          ? <button className="link-btn" onClick={() => navigate('/ledger')}>
                              {Number(c.invoiced_total).toLocaleString('en-GB')} ({c.invoice_count})
                            </button>
                          : <span className="muted">—</span>}
                      </td>
                      <td className="num">{Number(c.outstanding) > 0
                        ? <span className="pill warn">{Number(c.outstanding).toLocaleString('en-GB')}</span>
                        : <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </section>
          )}

          {interactions.length > 0 && (
            <section className="site-block">
              <h2>Interactions</h2>
              {interactions.map((i) => (
                <article key={i.id} className="card override">
                  <div className="override-head">
                    <strong>{i.subject}</strong>
                    <span className="pill mute">{i.kind.replace(/_/g, ' ')}</span>
                  </div>
                  {i.body && <p className="override-why">{i.body}</p>}
                  <div className="override-foot">
                    <button className="btn ghost sm" onClick={() => setEditingInteraction(i as unknown as Record<string, unknown>)}>Correct</button>
                    {' · '}
                    {new Date(i.occurred_at).toLocaleDateString('en-GB')}
                    {i.sentiment ? ` · ${i.sentiment}` : ''}
                    {i.follow_up_required ? ' · follow-up needed' : ''}
                  </div>
                </article>
              ))}
            </section>
          )}

          {intel.length > 0 && (
            <section className="site-block">
              <h2>Field intelligence</h2>
              {intel.map((i) => (
                <article key={i.id} className="card override">
                  <div className="override-head">
                    <strong>{i.headline}</strong>
                    {/* Only a synced record can be corrected: one still in the local queue has
                        no server row to update, and editing the overlay copy would diverge from
                        what eventually lands. */}
                    {isUuid(String(i.id)) && (
                      <button className="btn ghost sm" onClick={() => setEditingIntel(i)}>Correct</button>
                    )}
                  </div>
                  {(i.detail ?? i.detail) && <p className="override-why">{i.detail}</p>}
                  <div className="override-foot">
                    {String(i.source_type ?? i.sourceType ?? '').replace(/_/g, ' ')}
                    {' · '}{i.source_detail ?? i.sourceDetail}
                    {' · observed '}{i.observed_on ?? i.observedOn}
                  </div>
                </article>
              ))}
            </section>
          )}
        </div>

        {/* --- side --- */}
        <aside className="site-side">
          <section className="site-block">
            <h2>Actions</h2>
            <div className="side-actions">
              <button className="btn-save" onClick={() => setCapturing(true)}>Add intelligence</button>
              <button className="btn-cancel" onClick={() => setLogging(true)}>Log interaction</button>
              <button className="btn-cancel" onClick={() => setCreatingOpp(true)}>Create opportunity</button>
            </div>
          </section>

          {country && (
            <section className="site-block">
              <h2>{country.name}</h2>
              <dl className="side-dl">
                {/* Declared by the tenant. A transplant tenant shows donors per million and
                    donation model; a nephrology one shows prevalence, because donation data on
                    a nephrology practice page tells a prospect the software was built for
                    somebody else. */}
                {TENANT.vocabulary.countryFacts.map((f) => {
                  const raw = f.derive
                    ? f.derive(country as never as Record<string, unknown>)
                    : (country as never as Record<string, unknown>)[f.key as string];
                  const shown = raw == null || raw === ''
                    ? '—'
                    : String(raw).replace(/_/g, ' ');
                  return <React.Fragment key={f.label}><dt>{f.label}</dt><dd>{shown}</dd></React.Fragment>;
                })}
              </dl>
              {country.summary && <p className="block-text small">{country.summary}</p>}
            </section>
          )}

          {pathways.length > 0 && (
            <section className="site-block">
              <h2>Reimbursement routes</h2>
              {pathways.slice(0, 5).map((p) => (
                <div key={p.id} className="side-pathway">
                  <div className="sp-name">{p.routeName}</div>
                  <div className="sp-meta">
                    <span className={`pill ${p.difficulty === 'low' ? 'ok' : p.difficulty === 'very_high' ? 'risk' : 'mute'}`}>
                      {p.difficulty.replace('_', ' ')}
                    </span>
                    <span className="muted">{p.decisionBody}</span>
                  </div>
                </div>
              ))}
            </section>
          )}

          <section className="site-block">
            <h2>Record</h2>
            <dl className="side-dl">
              <dt>Reference</dt><dd><code>{site.id}</code></dd>
              <dt>Coordinates</dt>
              {/* Says so plainly rather than showing an empty field. 36 of the United States
                  laboratories sit in cities with no mapped centre to borrow a position from,
                  and a blank here reads as a rendering fault rather than a known gap. */}
              <dd>{!placed(site) ? 'Not placed: no coordinates recorded'
                    : site.geo === 'approx' ? 'Country centroid'
                    : `${site.lat.toFixed(3)}, ${site.lon.toFixed(3)}`}</dd>
              <dt>Source</dt><dd>{site.source ?? 'research'}</dd>
            </dl>
          </section>
        </aside>
      </div>

      {creatingOpp && (
        <NewOpportunity siteId={site.id} siteName={site.short || site.name}
                        onClose={() => setCreatingOpp(false)}
                        onDone={() => { setCreatingOpp(false); reload(); }} />
      )}
      {logging && (
        <LogInteraction siteId={site.id} siteName={site.short || site.name}
                        onClose={() => setLogging(false)}
                        onDone={() => { setLogging(false); reload(); }} />
      )}
      {capturing && (
        <CaptureIntelligence
          objectType="site" objectId={site.id} objectName={site.name}
          onClose={() => setCapturing(false)}
        />
      )}

        {editingIntel && (
          <EditRecord
            title="Correct this observation"
            table="atlas.intelligence"
            recordId={String(editingIntel.id)}
            value={{
              headline: editingIntel.headline ?? '',
              detail: editingIntel.detail ?? '',
              source_detail: editingIntel.source_detail ?? editingIntel.sourceDetail ?? '',
              observed_on: editingIntel.observed_on ?? editingIntel.observedOn ?? '',
              reliability: editingIntel.reliability ?? 'medium',
            }}
            fields={[
              { key: 'headline', label: 'What was observed' },
              { key: 'detail', label: 'Detail', type: 'textarea' },
              { key: 'source_detail', label: 'Where it came from',
                hint: 'Who said it, or which document it is in.' },
              { key: 'observed_on', label: 'Observed on', type: 'date' },
              { key: 'reliability', label: 'How reliable', type: 'select', options: [
                { value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' }, { value: 'unconfirmed', label: 'Unconfirmed' },
              ] },
            ]}
            onSave={async (patch) => {
              await updateIntelligence(String(editingIntel.id), patch);
              setEditingIntel(null);
            }}
            onClose={() => setEditingIntel(null)}
          />
        )}
        {editingInteraction && (
          <EditRecord
            title="Correct this interaction"
            table="crm.interactions"
            recordId={String(editingInteraction.id)}
            value={{
              subject: editingInteraction.subject ?? '',
              body: editingInteraction.body ?? '',
              sentiment: editingInteraction.sentiment ?? '',
            }}
            fields={[
              { key: 'subject', label: 'Subject' },
              { key: 'body', label: 'What was discussed', type: 'textarea' },
              { key: 'sentiment', label: 'Sentiment', type: 'select', options: [
                { value: '', label: 'Not recorded' },
                { value: 'positive', label: 'Positive' }, { value: 'neutral', label: 'Neutral' },
                { value: 'negative', label: 'Negative' },
              ] },
            ]}
            onSave={async (patch) => {
              await updateInteraction(String(editingInteraction.id), patch);
              if (site) fetchInteractions({ siteId: site.id }).then(setInteractions).catch(() => {});
              setEditingInteraction(null);
            }}
            onClose={() => setEditingInteraction(null)}
          />
        )}
    </div>
  );
}
